/**
 * 업무 프로젝트(PMS) J3c2a — 업무 상세 서랍의 «기록» 탭(변경 이력, 최신 순).
 *
 * 서버가 값까지 '표시용 문자열' 로 내려 주므로 여기서는 문장만 만든다(선택지 id 를 다시 풀지 않는다).
 * - 🔴 모르는 action·field 는 원문을 그대로 보인다 — 서버가 종류를 늘려도 줄이 비어 보이지 않게.
 * - `field_changed` 의 설명(description)은 old/new 가 늘 비어 있다 → 값 대신 한 문장으로 적는다.
 * - 참조자·체크리스트 변경은 서버가 기록하지 않아 여기 나오지 않는다(알려진 한계).
 * - reloadKey 가 바뀌면 다시 읽는다(상세에서 필드를 고치면 상위가 올려 준다).
 */
import { useEffect, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { errorText } from './errorText';
import { fmtMsgTime } from '../../utils/msgDate';
import { COLORS } from '../../utils/uiStyles';

const LOAD_FAIL = '기록을 불러오지 못했습니다.';
const EVENT_LIMIT = 100;        // client.listEvents 의 기본 limit 과 같아야 한다

// 서버 field 값(= services/task_pm_activity.FIELD_NAMES 의 이름) → 화면 라벨.
const FIELD_LABELS = {
  title: '제목',
  description: '설명',
  status: '상태',
  type: '유형',
  priority: '우선순위',
  start_date: '시작일',
  due_date: '마감일',
  assignee: '담당자',
  parent: '상위 업무',
};

const ACTION_TEXTS = {
  created: '업무를 만들었습니다',
  archived: '보관했습니다',
  unarchived: '보관을 해제했습니다',
  comment_added: '댓글을 남겼습니다',
  comment_deleted: '댓글을 지웠습니다',
};

export function eventText(event) {
  const action = event?.action || '';
  const oldValue = event?.old_value;
  const newValue = event?.new_value;
  if (action === 'field_changed') {
    const field = event?.field || '';
    if (field === 'description') return '설명을 바꿨습니다';
    return `${FIELD_LABELS[field] || field}: ${oldValue || '없음'} → ${newValue || '없음'}`;
  }
  if (action === 'attachment_added') return `파일 ${newValue || ''}을(를) 올렸습니다`;
  if (action === 'attachment_deleted') return `파일 ${oldValue || ''}을(를) 지웠습니다`;
  return ACTION_TEXTS[action] || action;
}

const subText = { fontSize: '11px', color: COLORS.textSub };
const mutedLine = { margin: 0, fontSize: '12px', color: COLORS.textSub };

export default function TaskPmEvents({ taskId, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let alive = true;
    taskPmAPI.listEvents(taskId)
      .then((res) => {
        if (!alive) return;
        setItems(res?.data?.items || []);
        setLoadError('');
      })
      .catch((e) => {
        if (!alive) return;
        setItems([]);
        setLoadError(errorText(e, LOAD_FAIL));
      });
    return () => { alive = false; };
  }, [taskId, reloadKey]);

  return (
    <div>
      {loadError && (
        <p role="alert" style={{ margin: '0 0 8px', fontSize: '12px', color: COLORS.dangerText }}>{loadError}</p>
      )}
      <ul aria-label="변경 기록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((event) => (
          <li key={event.id} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', padding: '5px 0', borderBottom: `1px solid ${COLORS.borderSoft}` }}>
            <span style={subText}>{fmtMsgTime(event.created_at)}</span>
            <span style={{ fontSize: '12px', color: COLORS.text }}>{event.actor_name || `사용자 #${event.actor_id}`}</span>
            <span style={{ fontSize: '12px', color: COLORS.text, wordBreak: 'break-word' }}>{eventText(event)}</span>
          </li>
        ))}
        {items.length === 0 && !loadError && <li style={mutedLine}>기록이 없습니다.</li>}
      </ul>
      {items.length >= EVENT_LIMIT && (
        <p style={{ ...mutedLine, marginTop: '6px' }}>{`최근 ${EVENT_LIMIT}건만 보입니다.`}</p>
      )}
    </div>
  );
}
